import { SSE, SSEOptions } from 'sse.js';
import { EenApiError } from '../api-types/common';
import api from '../api';

/**
 * Service for handling Server-Sent Events (SSE).
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events
 * sse.js provides "polyfill" for EventSource API to include headers and POST requests.
 * @see https://github.com/mpetazzoni/sse.js
 * @param PayloadType - The type of the payload.
 * @param MessageType - The type of the message event - required to pass type.
 */
export class SSEService<PayloadType = null, MessageType = unknown> {
  private sse: SSE | null = null;
  private url: string;
  private method: string;
  private payload: PayloadType;
  private reconnectInterval: number;

  constructor(url: string, method = 'GET', payload: PayloadType | null = null, reconnectInterval = 5000) {
    this.url = url;
    this.method = method;
    this.payload = payload as PayloadType;
    this.reconnectInterval = reconnectInterval;
    this.connect();
  }

  private openListener: ((event: Event) => void) | null = null;
  private closeListener: ((event: Event) => void) | null = null;
  private errorListener: ((event: Event) => void) | null = null;
  private messageListener: ((event: MessageEvent<MessageType>) => void) | null = null;
  private isClosing = false;

  private connect() {
    // Close existing connection
    if (this.sse) {
      this.sse.close();
      this.sse = null;
    }
    const options: SSEOptions = {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: api.headers().Authorization,
      },
      withCredentials: true,
      method: this.method,
    };

    if (this.payload) {
      try {
        options.payload = JSON.stringify(this.payload);
      } catch (error) {
        console.error('Failed to stringify payload:', error);
        return;
      }
    }

    this.sse = new SSE(this.url, options);

    // reconnect on error ex. network error
    this.sse.addEventListener('error', () => {
      if (this.isClosing) return;
      this.sse = null;
      // Reconnect after specified interval
      this.reconnectInterval *= 2; // Exponential backoff, to not overload the server
      setTimeout(() => this.connect(), this.reconnectInterval);
    });

    // Listen to the abort event manually
    if (this.closeListener) {
      window.addEventListener('beforeunload', this.triggerCloseListener);
    }
  }

  private triggerCloseListener = () => {
    if (this.closeListener) {
      const event = new Event('close');
      this.closeListener(event);
    }
  };

  onOpen(callback: (event: Event) => void) {
    // Remove existing listener
    if (this.openListener) {
      this.sse?.removeEventListener('open', this.openListener);
    }
    this.openListener = (event: Event) => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error occurred in onOpen callback:', error);
      }
    };
    this.sse?.addEventListener('open', this.openListener);
  }

  onClose(callback: (event: Event) => void) {
    this.closeListener = callback;
  }

  onError(callback: (event: Event & { data?: EenApiError }) => void) {
    // Remove existing listener
    if (this.errorListener) {
      this.sse?.removeEventListener('error', this.errorListener);
    }
    this.errorListener = (event: Event & { data?: EenApiError }) => {
      try {
        let updatedEvent = event;
        if (event.data && typeof event.data === 'string') {
          updatedEvent = { ...event, data: JSON.parse(event.data) };
        }
        callback(updatedEvent);
      } catch (error) {
        console.error('Error occurred in onError callback:', error);
      }
    };
    this.sse?.addEventListener('error', this.errorListener);
  }

  onMessage(callback: (event: MessageEvent<MessageType>) => void) {
    // Remove existing listener
    if (this.messageListener) {
      this.sse?.removeEventListener('message', this.messageListener);
    }
    this.messageListener = (event: MessageEvent<MessageType>) => {
      try {
        let updatedEvent = event;
        if (event.data && typeof event.data === 'string') {
          updatedEvent = { ...event, data: JSON.parse(event.data) };
        }
        callback(updatedEvent);
      } catch (error) {
        console.error('Error occurred in onMessage callback:', error);
      }
    };
    this.sse?.addEventListener('message', this.messageListener);
  }

  close() {
    if (this.sse) {
      this.isClosing = true;
      if (this.openListener) {
        this.sse.removeEventListener('open', this.openListener);
        this.openListener = null;
      }
      if (this.errorListener) {
        this.sse.removeEventListener('error', this.errorListener);
        this.errorListener = null;
      }
      if (this.messageListener) {
        this.sse.removeEventListener('message', this.messageListener);
        this.messageListener = null;
      }
      if (this.closeListener) {
        window.removeEventListener('beforeunload', this.triggerCloseListener);
        this.triggerCloseListener();
        this.closeListener = null;
      }
      this.sse.close();
    }
  }
}
